import * as Yup from "yup";

export const RFID_MAX_FILE_SIZE = 5 * 1024 * 1024;
export const RFID_ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

// IMEI/SL/MFG.NO can be alphanumeric (not strictly 15 digits).
export const IMEI_PATTERN = /^[a-zA-Z0-9]+$/;
export const IMEI_MAX_LENGTH = 30;

export const RFID_TYPE_PATTERN = /^[a-zA-Z0-9\s\-_]+$/;

export const formatRfidTypeInput = (value = "") =>
  value.replace(/[^a-zA-Z0-9\s\-_]/g, "").slice(0, 100);

export const formatImeiInput = (value = "") =>
  value.replace(/[^a-zA-Z0-9]/g, "").slice(0, IMEI_MAX_LENGTH);

export const OTHER_NO_MAX_LENGTH = 50;
export const LOCATION_NAME_MAX_LENGTH = 100;

/** List/display placeholders must not be treated as real field values in forms. */
export const isRfidDisplayPlaceholder = (value) => {
  const trimmed = String(value ?? "").trim();
  return !trimmed || trimmed === "-" || trimmed.toUpperCase() === "N/A";
};

/** Normalize list/display values for edit form + uniqueness checks (same as Add). */
export const toRfidEditableValue = (value = "") =>
  isRfidDisplayPlaceholder(value) ? "" : String(value).trim();

// Accept all characters (including special chars like @ $ %).
export const formatLocationNameInput = (value = "") =>
  String(value ?? "").slice(0, LOCATION_NAME_MAX_LENGTH);

export const INSURANCE_NO_PATTERN = /^[a-zA-Z0-9]+$/;
export const INSURANCE_PROVIDER_PATTERN = /^[a-zA-Z\s]+$/;

export const formatInsuranceNoInput = (value = "") =>
  value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 30);

// Accept all characters (including special chars like @ $ %).
export const formatOtherNoInput = (value = "") =>
  String(value ?? "").slice(0, OTHER_NO_MAX_LENGTH);

export const formatInsuranceProviderInput = (value = "") =>
  value.replace(/[^a-zA-Z\s]/g, "").slice(0, 100);

export const VEHICLE_ASSET_TYPE_IDS = new Set(["1", "3"]);

export const VEHICLE_ASSET_TYPE_NAMES = new Set([
  "RFID Reader - RC Vehicle",
  "RFID Tag - Vehicles",
]);

export const isVehicleAssetType = (rfidTypeId, assetTypes = []) => {
  const typeId = String(rfidTypeId ?? "").trim();
  if (!typeId) return false;
  if (VEHICLE_ASSET_TYPE_IDS.has(typeId)) return true;

  const selectedType = assetTypes.find(
    (type) =>
      String(type.assetTypeId ?? "") === typeId ||
      String(type.rfidTypeId ?? "") === typeId
  );
  const typeName = (
    selectedType?.assetTypeName ??
    selectedType?.rfidTypeName ??
    ""
  ).trim();
  if (!typeName) return false;
  if (VEHICLE_ASSET_TYPE_NAMES.has(typeName)) return true;

  const normalized = typeName.toLowerCase();
  return (
    normalized.includes("rc vehicle") ||
    normalized.includes("tag - vehicles") ||
    normalized.includes("tag - vehicle")
  );
};

const parseDateForCompare = (value) => {
  if (!value) return null;
  const selected = new Date(value);
  if (Number.isNaN(selected.getTime())) return null;
  selected.setHours(0, 0, 0, 0);
  return selected;
};

const getRfidRecordIds = (record) => {
  if (!record) return [];
  return [record.rfidMasterId, record.assetRfidId, record.rfidId]
    .filter((id) => id !== null && id !== undefined && String(id).trim() !== "")
    .map((id) => String(id));
};

const getRfidRecordCodes = (record) => {
  if (!record) return [];
  return [record.rfidCode, record.assetCode, record.assetRfidCode]
    .map((code) => String(code ?? "").trim().toLowerCase())
    .filter((code) => code && code !== "-" && code !== "n/a");
};

const getCurrentRfidIds = (isEditMode, editRow) =>
  isEditMode ? getRfidRecordIds(editRow) : [];

const isSameRfidRecord = (record, currentIds = [], editRow = null) => {
  if (currentIds.length) {
    if (getRfidRecordIds(record).some((id) => currentIds.includes(id))) {
      return true;
    }
  }
  // Fallback when list/edit IDs differ: match by asset/RFID code.
  if (editRow) {
    const currentCodes = getRfidRecordCodes(editRow);
    if (!currentCodes.length) return false;
    return getRfidRecordCodes(record).some((code) => currentCodes.includes(code));
  }
  return false;
};

const getComparableImei = (item) =>
  toRfidEditableValue(item?.imeiSlMfgNo ?? item?.imeiNo ?? "");

const getComparableOtherNo = (item) =>
  toRfidEditableValue(item?.otherNo ?? "");

const getErrorText = (data, fallback = "") => {
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  return fallback;
};

export const isRfidSaveSuccess = (data) => {
  if (!data) return false;
  if (data?.result != null && Number(data.result) === 1) return true;
  if (data?.success === true || data?.isSuccess === true) return true;
  const message = getErrorText(data, "").toLowerCase();
  return message.includes("successfully");
};

export const mapRfidBackendErrors = (data, fallbackMessage = "") => {
  if (isRfidSaveSuccess(data)) return {};

  const message = getErrorText(data, fallbackMessage);
  const text = message.toLowerCase();
  const errors = {};

  if (!text || text.includes("successfully")) return errors;

  if (text.includes("imei")) {
    errors.imeiSlMfgNo = message;
  } else if (text.includes("other") && text.includes("no")) {
    errors.otherNo = message;
  } else if (
    text.includes("rfid") &&
    (text.includes("duplicate") ||
      (text.includes("code") && !text.includes("asset code")))
  ) {
    errors.rfidDeviceName = message;
  } else if (text.includes("device")) {
    errors.rfidDeviceName = message;
  } else if (text.includes("category")) {
    errors.assetCategoryId = message;
  } else if (text.includes("zone")) {
    errors.zoneId = message;
  } else if (text.includes("ward") || text.includes("circle")) {
    errors.circleId = message;
  } else if (text.includes("installed area") || text.includes("asset area")) {
    errors.assetAreaId = message;
  } else if (text.includes("collection type")) {
    errors.collectionTypeId = message;
  } else if (text.includes("vehicle type")) {
    errors.vehicleTypeId = message;
  } else if (text.includes("vehicle capacity")) {
    errors.vehicleCapacityId = message;
  } else if (text.includes("owner type")) {
    errors.ownerTypeId = message;
  } else if (text.includes("location") || text.includes("landmark")) {
    errors.locationLandmark = message;
  } else if (text.includes("latitude") || text.includes("lat")) {
    errors.latitude = message;
  } else if (text.includes("longitude") || text.includes("lng")) {
    errors.longitude = message;
  } else if (text.includes("date") || text.includes("installation")) {
    errors.dateOfInstallation = message;
  } else if (text.includes("image") || text.includes("file")) {
    errors.imageFile = message;
  } else if (text.includes("duplicate") || text.includes("already exist")) {
    if (text.includes("other")) {
      errors.otherNo = message;
    } else if (
      text.includes("rfid") ||
      text.includes("code") ||
      text.includes("device")
    ) {
      errors.rfidDeviceName = message;
    } else {
      errors.imeiSlMfgNo = message;
    }
  }

  if (data?.errors && typeof data.errors === "object") {
    Object.entries(data.errors).forEach(([key, value]) => {
      const fieldMap = {
        assetcategoryid: "assetCategoryId",
        // rfdevicename: "rfidDeviceName",
        imeino: "imeiSlMfgNo",
        location: "locationLandmark",
        zoneid: "zoneId",
        circleid: "circleId",
        assetareaid: "assetAreaId",
        otherno: "otherNo",
        collectiontypeid: "collectionTypeId",
        vehicletypeid: "vehicleTypeId",
        vehiclecapacityid: "vehicleCapacityId",
        ownertypeid: "ownerTypeId",
        installdate: "dateOfInstallation",
        dateofpurchase: "purchaseDate",
        warrantyexpdate: "warrantyExpireDate",
        lat: "latitude",
        lng: "longitude",
        imagefile: "imageFile",
      };
      const field = fieldMap[key.toLowerCase()] || key;
      errors[field] = Array.isArray(value) ? value[0] : value;
    });
  }

  return errors;
};

const vehicleFieldRequired = (label, assetTypes = []) =>
  Yup.mixed().test({
    name: `vehicle-${label.toLowerCase().replace(/\s+/g, "-")}-required`,
    message: `${label} is Required`,
    skipAbsent: false,
    test(value) {
      if (!isVehicleAssetType(this.parent?.rfidTypeId, assetTypes)) return true;
      return Boolean(String(value ?? "").trim());
    },
  });

/** Sync Formik validate helper — ensures all vehicle required errors surface together. */
export const getVehicleFieldErrors = (values = {}, assetTypes = []) => {
  const errors = {};
  if (!isVehicleAssetType(values.rfidTypeId, assetTypes)) return errors;

  if (!String(values.vehicleTypeId ?? "").trim()) {
    errors.vehicleTypeId = "Vehicle Type is Required";
  }
  if (!String(values.vehicleCapacityId ?? "").trim()) {
    errors.vehicleCapacityId = "Vehicle Capacity is Required";
  }
  if (!String(values.ownerTypeId ?? "").trim()) {
    errors.ownerTypeId = "Owner Type is Required";
  }
  return errors;
};

export const getRfidValidationSchema = ({
  wardData = [],
  rfidDevices = [],
  assetTypes = [],
  isEditMode = false,
  editRow = null,
}) => {
  const currentIds = getCurrentRfidIds(isEditMode, editRow);

  return Yup.object().shape({
    rfidTypeId: Yup.string().required("Asset Type is Required"),
    assetCategoryId: Yup.string().required("Asset Category is Required"),
    collectionTypeId: Yup.string().required("Collection Type is required"),
    vehicleTypeId: vehicleFieldRequired("Vehicle Type", assetTypes),
    vehicleCapacityId: vehicleFieldRequired("Vehicle Capacity", assetTypes),
    ownerTypeId: vehicleFieldRequired("Owner Type", assetTypes),
    // rfidDeviceName: Yup.string()
    //   .trim()
    //   .required("Device Name is Required")
    //   .max(100, "Maximum 100 characters allowed.")
    //   .matches(
    //     RFID_TYPE_PATTERN,
    //     "Only alphabets, numbers, spaces, hyphen (-), and underscore (_) are allowed."
    //   )
    //   .test(
    //     "unique-rfid-code",
    //     "Duplicate RFID Code is not allowed",
    //     function (value) {
    //       const trimmed = value?.trim();
    //       if (!trimmed) return true;

    //       return !rfidDevices.some(
    //         (item) =>
    //           !isSameRfidRecord(item, currentId) &&
    //           (item.rfidCode?.trim().toLowerCase() === trimmed.toLowerCase() ||
    //             item.rfidDeviceName?.trim().toLowerCase() === trimmed.toLowerCase())
    //       );
    //     }
    //   ),
    imeiSlMfgNo: Yup.string()
      .trim()
      .required("IMEI Number is Required")
      .matches(IMEI_PATTERN, "IMEI Number must be alphanumeric.")
      .max(IMEI_MAX_LENGTH, `IMEI Number cannot exceed ${IMEI_MAX_LENGTH} characters.`)
      .test(
        "unique-imei",
        "IMEI Number already exists",
        function (value) {
          const trimmed = toRfidEditableValue(value);
          if (!trimmed) return true;

          // Same as Add rules; on Edit allow keeping this record's own IMEI.
          if (isEditMode && editRow) {
            const original = getComparableImei(editRow);
            if (original && original.toLowerCase() === trimmed.toLowerCase()) {
              return true;
            }
          }

          return !rfidDevices.some((item) => {
            if (isSameRfidRecord(item, currentIds, editRow)) return false;
            const existing = getComparableImei(item);
            return (
              existing &&
              existing.toLowerCase() === trimmed.toLowerCase()
            );
          });
        }
      ),
    locationLandmark: Yup.string()
      .trim()
      .required("Location Name is Required")
      .max(
        LOCATION_NAME_MAX_LENGTH,
        `Location Name must be at most ${LOCATION_NAME_MAX_LENGTH} characters`
      ),
    zoneId: Yup.string().required("Zone is Required"),
    circleId: Yup.string()
      .required("Ward is Required")
      .test(
        "ward-in-zone",
        "Ward must belong to selected Zone",
        function (value) {
          const { zoneId } = this.parent;
          if (!value || !zoneId || !wardData.length) return true;

          return wardData.some(
            (ward) =>
              String(ward.circleId) === String(value) &&
              String(ward.zoneId) === String(zoneId)
          );
        }
      ),
    assetAreaId: Yup.string().required("Installed Area is Required"),
    dateOfInstallation: Yup.string()
      .required("Date Of Installation is Required")
      .test(
        "not-future-date",
        "Installation Date cannot be a future date",
        (value) => {
          if (!value) return false;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const selected = new Date(value);
          if (Number.isNaN(selected.getTime())) return false;
          selected.setHours(0, 0, 0, 0);
          return selected <= today;
        }
      ),
    otherNo: Yup.string()
      .trim()
      .required("Other No is required.")
      .min(3, "Other No must contain at least 3 characters.")
      .max(
        OTHER_NO_MAX_LENGTH,
        `Other No cannot exceed ${OTHER_NO_MAX_LENGTH} characters.`
      )
      .test(
        "unique-other-no",
        "Other No already exists",
        function (value) {
          const trimmed = toRfidEditableValue(value);
          if (!trimmed) return true;

          // Same as Add rules; on Edit allow keeping this record's own Other No.
          if (isEditMode && editRow) {
            const original = getComparableOtherNo(editRow);
            if (original && original.toLowerCase() === trimmed.toLowerCase()) {
              return true;
            }
          }

          return !rfidDevices.some((item) => {
            if (isSameRfidRecord(item, currentIds, editRow)) return false;
            const existing = getComparableOtherNo(item);
            return (
              existing &&
              existing.toLowerCase() === trimmed.toLowerCase()
            );
          });
        }
      ),
    warrantyExpireDate: Yup.string()
      .notRequired()
      .test(
        "after-purchase-date",
        "Warranty Expire Date should be after or equal to the Purchase Date",
        function (value) {
          const { purchaseDate } = this.parent;
          if (!value || !purchaseDate) return true;
          const warranty = parseDateForCompare(value);
          const purchase = parseDateForCompare(purchaseDate);
          if (!warranty || !purchase) return true;
          return warranty >= purchase;
        }
      ),
    purchaseDate: Yup.string()
      .notRequired()
      .test(
        "not-future-purchase-date",
        "Purchase Date cannot be a future date",
        (value) => {
          if (!value) return true;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const selected = parseDateForCompare(value);
          if (!selected) return false;
          return selected <= today;
        }
      )
      .test(
        "not-after-installation",
        "Purchase Date should not be after the Date of Installation",
        function (value) {
          const { dateOfInstallation } = this.parent;
          if (!value || !dateOfInstallation) return true;
          const purchase = parseDateForCompare(value);
          const installation = parseDateForCompare(dateOfInstallation);
          if (!purchase || !installation) return true;
          return purchase <= installation;
        }
      ),
    insuranceNo: Yup.string()
      .trim()
      .notRequired()
      .max(30, "Maximum 30 characters allowed.")
      .matches(INSURANCE_NO_PATTERN, "Only letters and numbers are allowed.", {
        excludeEmptyString: true,
      }),
    insuranceValidity: Yup.string()
      .notRequired()
      .test(
        "after-purchase-date",
        "Insurance Validity should be after or equal to the Purchase Date",
        function (value) {
          const { purchaseDate } = this.parent;
          if (!value || !purchaseDate) return true;
          const validity = parseDateForCompare(value);
          const purchase = parseDateForCompare(purchaseDate);
          if (!validity || !purchase) return true;
          return validity >= purchase;
        }
      ),
    insuranceProvider: Yup.string()
      .trim()
      .notRequired()
      .max(100, "Maximum 100 characters allowed.")
      .matches(INSURANCE_PROVIDER_PATTERN, "Only alphabets and spaces are allowed.", {
        excludeEmptyString: true,
      }),
    latitude: Yup.number()
      .typeError("Latitude is Required")
      .required("Latitude is Required")
      .min(-90, "Latitude must be between -90 and +90")
      .max(90, "Latitude must be between -90 and +90"),
    longitude: Yup.number()
      .typeError("Longitude is Required")
      .required("Longitude is Required")
      .min(-180, "Longitude must be between -180 and +180")
      .max(180, "Longitude must be between -180 and +180"),
    installationRemarks: Yup.string().max(
      500,
      "Maximum 500 characters allowed."
    ),
    imageFile: Yup.mixed()
      .nullable()
      .test(
        "file-format",
        "Uploaded file format is not supported",
        (value) => !value || RFID_ALLOWED_FILE_TYPES.includes(value.type)
      )
      .test(
        "file-size",
        "Maximum upload size exceeded",
        (value) => !value || value.size <= RFID_MAX_FILE_SIZE
      ),
  });
};
